import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import Spline from '@splinetool/react-spline';
import { AlertTriangle, Shield, TrendingUp, Brain, Lock, Scale, Search, Eye, Flag, CheckCircle2, Calendar, MapPin, Mail, User, Building, ExternalLink, ChevronUp, Mic, Users, Menu, X, Crown, Globe, Rocket, Award, FileCheck, Globe2 } from 'lucide-react';
import { FaTwitter, FaInstagram, FaLinkedin, FaGlobe } from 'react-icons/fa';
import { topics, whyAttend, venue } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { 
  useScrollProgress, 
  useScrollToTop, 
  useStickyHeader, 
  useIntersectionObserver,
  useSmoothScroll 
} from '../hooks/useAnimations';
import { initAnalytics, trackButtonClick, trackRegistrationClick } from '../utils/analytics';

export const BACKEND_URL = 'https://kicrf-backend-production.up.railway.app';

const iconMap = {
  AlertTriangle,
  Shield,
  TrendingUp,
  Brain,
  Lock,
  Scale,
  Search,
  Eye,
  Flag,
  FileCheck,
  Globe2
};

const Home = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  // API data states
  const [speakers, setSpeakers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [mediaPartners, setMediaPartners] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [siteSettings, setSiteSettings] = useState({
    register_button_url: 'https://forms.office.com/Pages/ResponsePage.aspx?id=N5o-n14Uc0qrkY_28dSZpMwhjXeqEA1AklK8Zo_IMaxUM0hLTVY4UVM4UDM2UTlQT0xFNThGUjVFUC4u',
    program_button_url: 'https://cyberforumkyiv.org/',
    speaker_register_url: 'https://forms.office.com/Pages/ResponsePage.aspx?id=N5o-n14Uc0qrkY_28dSZpMwhjXeqEA1AklK8Zo_IMaxUM0hLTVY4UVM4UDM2UTlQT0xFNThGUjVFUC4u',
    participant_register_url: 'https://forms.office.com/Pages/ResponsePage.aspx?id=N5o-n14Uc0qrkY_28dSZpMwhjXeqEA1AklK8Zo_IMaxUM0hLTVY4UVM4UDM2UTlQT0xFNThGUjVFUC4u'
  });
  const [loading, setLoading] = useState(true);
  const [flippedCards, setFlippedCards] = useState({});

  // Animation hooks
  const scrollProgress = useScrollProgress();
  const { showButton, scrollToTop } = useScrollToTop();
  const isScrolled = useStickyHeader();
  useIntersectionObserver();
  useSmoothScroll();

  // Smooth scroll function
  const smoothScrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerHeight = 80;
      const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };


  // Toggle flip for speaker cards
  const toggleCardFlip = (speakerId) => {
    setFlippedCards(prev => ({
      ...prev,
      [speakerId]: !prev[speakerId]
    }));
  };

  // Load data from API
  React.useEffect(() => {
    const loadData = async () => {
      try {
        // Load speakers
        const speakersRes = await fetch(`${BACKEND_URL}/api/speakers`);
        if (speakersRes.ok) {
          const data = await speakersRes.json();
          setSpeakers(data);
        }

        // Load partners
        const partnersRes = await fetch(`${BACKEND_URL}/api/partners`);
        if (partnersRes.ok) {
          const data = await partnersRes.json();
          setPartners(data);
        }

        // Load media partners
        const mediaPartnersRes = await fetch(`${BACKEND_URL}/api/media-partners`);
        if (mediaPartnersRes.ok) {
          const data = await mediaPartnersRes.json();
          setMediaPartners(data);
        }

        // Load schedule
        const scheduleRes = await fetch(`${BACKEND_URL}/api/schedule`);
        if (scheduleRes.ok) {
          const data = await scheduleRes.json();
          setSchedule(data);
        }

        // Load site settings
        const settingsRes = await fetch(`${BACKEND_URL}/api/settings`);
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setSiteSettings(data);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Помилка завантаження даних');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    
    // Initialize analytics tracking
    const cleanup = initAnalytics();
    return cleanup;
  }, []);

  const handleRegisterClick = () => {
    trackButtonClick('hero_register_cta', { button_location: 'hero', button_type: 'primary' });
    window.open(siteSettings.cta_become_part_url || siteSettings.register_button_url, '_blank');
  };

  const handleProgramClick = () => {
    trackButtonClick('hero_program_cta', { button_location: 'hero', button_type: 'secondary' });
    window.open(siteSettings.cta_learn_more_url || siteSettings.program_button_url, '_blank');
  };

  const handleSpeakerRegister = () => {
    trackRegistrationClick('speaker');
    window.open(siteSettings.speaker_register_url, '_blank');
  };

  const handleParticipantRegister = () => {
    trackRegistrationClick('participant');
    window.open(siteSettings.participant_register_url, '_blank');
  };

  const handleMainSiteRedirect = () => {
    window.open('https://cyberforumkyiv.org/', '_blank');
  };

  return (
    <div className="dark-theme">
      {/* Scroll Progress Bar */}
      <div 
        className="scroll-progress" 
        style={{ width: `${scrollProgress}%` }}
      ></div>

      {/* Header */}
      <header className={`dark-header ${isScrolled ? 'scrolled' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div 
            style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#00ffff',
              margin: 0,
              cursor: 'pointer',
              letterSpacing: '1px',
              borderLeft: '3px solid #00ffff',
              paddingLeft: '10px'
            }}
            className="logo-text"
            onClick={handleMainSiteRedirect}
          >
            KICRF
          </div>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="dark-nav">
          <a href="#about" className="dark-nav-link" onClick={(e) => { e.preventDefault(); smoothScrollTo('about'); }}>{t('nav.about')}</a>
          <a href="#topics" className="dark-nav-link" onClick={(e) => { e.preventDefault(); smoothScrollTo('topics'); }}>{t('nav.topics')}</a>
          <a href="#speakers" className="dark-nav-link" onClick={(e) => { e.preventDefault(); smoothScrollTo('speakers'); }}>{t('nav.speakers')}</a>
          <a href="#program" className="dark-nav-link" onClick={(e) => { e.preventDefault(); smoothScrollTo('program'); }}>{t('nav.program')}</a>
          <a href="#partners" className="dark-nav-link" onClick={(e) => { e.preventDefault(); smoothScrollTo('partners'); }}>{t('nav.partners')}</a>
          <a href="#register" className="dark-nav-link active" onClick={(e) => { e.preventDefault(); smoothScrollTo('register'); }}>{t('nav.register')}</a>
          <a href="https://cyberforumkyiv.org/" target="_blank" rel="noopener noreferrer" className="dark-nav-link" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {t('nav.mainSite')} <ExternalLink size={14} />
          </a>
          <LanguageSwitcher />
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </header>

      {/* Mobile Navigation */}
      <nav className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
        <a 
          href="#about" 
          className="mobile-nav-link"
          onClick={(e) => { e.preventDefault(); smoothScrollTo('about'); setMobileMenuOpen(false); }}
        >
          {t('nav.about')}
        </a>
        <a 
          href="#topics" 
          className="mobile-nav-link"
          onClick={(e) => { e.preventDefault(); smoothScrollTo('topics'); setMobileMenuOpen(false); }}
        >
          {t('nav.topics')}
        </a>
        <a 
          href="#speakers" 
          className="mobile-nav-link"
          onClick={(e) => { e.preventDefault(); smoothScrollTo('speakers'); setMobileMenuOpen(false); }}
        >
          {t('nav.speakers')}
        </a>
        <a 
          href="#program" 
          className="mobile-nav-link"
          onClick={(e) => { e.preventDefault(); smoothScrollTo('program'); setMobileMenuOpen(false); }}
        >
          {t('nav.program')}
        </a>
        <a 
          href="#partners" 
          className="mobile-nav-link"
          onClick={(e) => { e.preventDefault(); smoothScrollTo('partners'); setMobileMenuOpen(false); }}
        >
          {t('nav.partners')}
        </a>
        <a 
          href="#register" 
          className="mobile-nav-link active"
          onClick={(e) => { e.preventDefault(); smoothScrollTo('register'); setMobileMenuOpen(false); }}
        >
          {t('nav.register')}
        </a>
        <a 
          href="https://cyberforumkyiv.org/" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="mobile-nav-link"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
        >
          {t('nav.mainSite')} <ExternalLink size={16} />
        </a>
        <LanguageSwitcher />
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-left">
            <div className="hero-badge">
              {t('hero.badge')}
            </div>
            <h1 className="hero-title">{t('hero.title')}</h1>
            <p className="hero-subtitle">{t('hero.subtitle')}</p>
            <p className="hero-description">
              {t('hero.description')}
            </p>
            <div className="hero-cta">
              <Button className="btn-primary" onClick={handleRegisterClick}>
                {t('hero.ctaPrimary')}
              </Button>
              <Button className="btn-secondary" onClick={handleProgramClick}>
                {t('hero.ctaSecondary')}
              </Button>
            </div>
            <div className="hero-date">
              <Calendar className="icon-red" />
              <span>{t('venue.dateName')} • {t('venue.cityName')}</span>
            </div>
          </div>
          <div className="hero-right">
            <div className="spline-container">
              <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
            </div>
          </div>
        </div>
        <div className="hero-grid-overlay"></div>
      </section>

      {/* Crypto Mission Section - First State Crypto Forum */}
      <section className="crypto-mission-section fade-in">
        <div className="crypto-mission-container">
          <div className="crypto-mission-header">
            <div className="crypto-mission-badge">
              <Rocket size={18} />
              <span>{t('hero.subtitle')}</span>
            </div>
            <h2 className="crypto-mission-title">{t('cryptoMission.title')}</h2>
            <p className="crypto-mission-subtitle">{t('cryptoMission.subtitle')}</p>
          </div>
          
          <p className="crypto-mission-main-text">
            {t('cryptoMission.mainText')}
          </p>
          
          <div className="crypto-mission-highlight">
            {t('cryptoMission.highlight')}
          </div>
          
          <div className="crypto-mission-cards">
            <div className="crypto-mission-card">
              <div className="crypto-mission-card-icon">
                <Crown />
              </div>
              <div className="crypto-mission-card-badge">{t('cryptoMission.cards.government.badge')}</div>
              <h3>{t('cryptoMission.cards.government.title')}</h3>
              <p>{t('cryptoMission.cards.government.description')}</p>
            </div>
            
            <div className="crypto-mission-card">
              <div className="crypto-mission-card-icon">
                <TrendingUp />
              </div>
              <div className="crypto-mission-card-badge">{t('cryptoMission.cards.market.badge')}</div>
              <h3>{t('cryptoMission.cards.market.title')}</h3>
              <p>{t('cryptoMission.cards.market.description')}</p>
            </div>
            
            <div className="crypto-mission-card">
              <div className="crypto-mission-card-icon">
                <Globe />
              </div>
              <div className="crypto-mission-card-badge">{t('cryptoMission.cards.media.badge')}</div>
              <h3>{t('cryptoMission.cards.media.title')}</h3>
              <p>{t('cryptoMission.cards.media.description')}</p>
            </div>
            
            <div className="crypto-mission-card">
              <div className="crypto-mission-card-icon">
                <Award />
              </div>
              <div className="crypto-mission-card-badge">{t('cryptoMission.cards.future.badge')}</div>
              <h3>{t('cryptoMission.cards.future.title')}</h3>
              <p>{t('cryptoMission.cards.future.description')}</p>
            </div>
          </div>
          
          <div className="crypto-mission-stats">
            <div className="crypto-mission-stat">
              <div className="crypto-mission-stat-value">{siteSettings.stats_experts || t('cryptoMission.stats.speakers')}</div>
              <div className="crypto-mission-stat-label">{t('cryptoMission.stats.speakersLabel')}</div>
            </div>
            <div className="crypto-mission-stat">
              <div className="crypto-mission-stat-value">{siteSettings.stats_attendees || t('cryptoMission.stats.attendees')}</div>
              <div className="crypto-mission-stat-label">{t('cryptoMission.stats.attendeesLabel')}</div>
            </div>
            <div className="crypto-mission-stat">
              <div className="crypto-mission-stat-value">{siteSettings.stats_partners || t('cryptoMission.stats.partners')}</div>
              <div className="crypto-mission-stat-label">{t('cryptoMission.stats.partnersLabel')}</div>
            </div>
            <div className="crypto-mission-stat">
              <div className="crypto-mission-stat-value">{siteSettings.stats_countries || t('cryptoMission.stats.countries')}</div>
              <div className="crypto-mission-stat-label">{t('cryptoMission.stats.countriesLabel')}</div>
            </div>
          </div>
          
          <div className="crypto-mission-cta">
            <Button className="btn-primary" onClick={handleRegisterClick}>
              {t('cryptoMission.cta')}
            </Button>
            <Button className="btn-secondary" onClick={handleProgramClick}>
              {t('cryptoMission.ctaSecondary')}
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-dark fade-in">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('about.title')}</h2>
            <div className="red-line"></div>
          </div>
          <div className="about-content">
            <p className="about-text">
              {t('about.description')}
            </p>
            <div className="about-grid about-grid-6">
              <div className="about-item">
                <Flag className="about-icon" />
                <h3>{t('about.items.redFlags.title')}</h3>
                <p>{t('about.items.redFlags.description')}</p>
              </div>
              <div className="about-item">
                <Search className="about-icon" />
                <h3>{t('about.items.osint.title')}</h3>
                <p>{t('about.items.osint.description')}</p>
              </div>
              <div className="about-item">
                <Shield className="about-icon" />
                <h3>{t('about.items.investigation.title')}</h3>
                <p>{t('about.items.investigation.description')}</p>
              </div>
              <div className="about-item">
                <AlertTriangle className="about-icon" />
                <h3>{t('about.items.threatIntel.title')}</h3>
                <p>{t('about.items.threatIntel.description')}</p>
              </div>
              <div className="about-item">
                <FileCheck className="about-icon" />
                <h3>{t('about.items.compliance.title')}</h3>
                <p>{t('about.items.compliance.description')}</p>
              </div>
              <div className="about-item">
                <Globe2 className="about-icon" />
                <h3>{t('about.items.darkweb.title')}</h3>
                <p>{t('about.items.darkweb.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Topics */}
      <section id="topics" className="section-dark fade-in">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('topics.title')}</h2>
            <div className="red-line"></div>
          </div>
          <div className="topics-grid">
            {t('topicsList', { returnObjects: true }).map((topic, index) => {
              const IconComponent = iconMap[topics[index]?.icon || 'AlertTriangle'];
              return (
                <Card key={index} className="topic-card">
                  <CardContent className="topic-card-content">
                    <div className="topic-icon-wrapper">
                      <IconComponent className="topic-icon" />
                    </div>
                    <h3 className="topic-title">{topic.title}</h3>
                    <p className="topic-description">{topic.description}</p>
                    <div className="topic-flag">{topic.flag}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Speakers Section */}
      <section id="speakers" className="section-dark fade-in">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('speakers.title')}</h2>
            <div className="red-line"></div>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#00ffff' }}>
              Завантаження спікерів...
            </div>
          ) : speakers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              {t('speakers.comingSoon')}
            </div>
          ) : (
            <div className="speakers-grid">
              {speakers.map((speaker) => (
              <div key={speaker.id} className="speaker-card-container">
                <div 
                  className={`speaker-card-flipper ${flippedCards[speaker.id] ? 'flipped' : ''}`}
                  onClick={() => toggleCardFlip(speaker.id)}
                >
                  {/* Front Side */}
                  <Card className="speaker-card speaker-card-front">
                    <div className="speaker-image-wrapper">
                      <img src={speaker.photo} alt={speaker.name} className="speaker-image" />
                      <div className="speaker-overlay"></div>
                    </div>
                    <CardContent className="speaker-content">
                      <h3 className="speaker-name">{speaker.name}</h3>
                      <p className="speaker-title">{i18n.language === 'en' && speaker.title_en ? speaker.title_en : speaker.title}</p>
                      <p className="speaker-company">{i18n.language === 'en' && speaker.company_en ? speaker.company_en : speaker.company}</p>
                      
                      {/* Social Links */}
                      {speaker.social_links && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px', 
                          marginTop: '12px',
                          justifyContent: 'center'
                        }}>
                          {speaker.social_links.twitter && (
                            <a 
                              href={speaker.social_links.twitter} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#00ffff', 
                                transition: 'transform 0.2s',
                                display: 'inline-block'
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <FaTwitter size={20} />
                            </a>
                          )}
                          {speaker.social_links.instagram && (
                            <a 
                              href={speaker.social_links.instagram} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#00ffff', 
                                transition: 'transform 0.2s',
                                display: 'inline-block'
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <FaInstagram size={20} />
                            </a>
                          )}
                          {speaker.social_links.linkedin && (
                            <a 
                              href={speaker.social_links.linkedin} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#00ffff', 
                                transition: 'transform 0.2s',
                                display: 'inline-block'
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <FaLinkedin size={20} />
                            </a>
                          )}
                          {speaker.social_links.website && (
                            <a 
                              href={speaker.social_links.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#00ffff', 
                                transition: 'transform 0.2s',
                                display: 'inline-block'
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <FaGlobe size={20} />
                            </a>
                          )}
                        </div>
                      )}
                      
                      {/* Click hint */}
                      <div style={{
                        marginTop: '16px',
                        fontSize: '12px',
                        color: '#00ffff',
                        textAlign: 'center',
                        opacity: 0.7
                      }}>
                        {t('speakers.clickForBio') || '🔄 Click to see bio'}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Back Side - Biography */}
                  <Card className="speaker-card speaker-card-back">
                    <CardContent className="speaker-bio-content">
                      <div className="speaker-bio-header">
                        <h3 className="speaker-name">{speaker.name}</h3>
                        <p className="speaker-title">{i18n.language === 'en' && speaker.title_en ? speaker.title_en : speaker.title}</p>
                      </div>
                      
                      <div className="speaker-bio-text">
                        {i18n.language === 'en' && speaker.bio_en ? speaker.bio_en : speaker.bio}
                      </div>
                      
                      {/* Social Links on back */}
                      {speaker.social_links && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px', 
                          marginTop: '16px',
                          justifyContent: 'center'
                        }}>
                          {speaker.social_links.twitter && (
                            <a 
                              href={speaker.social_links.twitter} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#00ffff', transition: 'transform 0.2s', display: 'inline-block' }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <FaTwitter size={18} />
                            </a>
                          )}
                          {speaker.social_links.instagram && (
                            <a 
                              href={speaker.social_links.instagram} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#00ffff', transition: 'transform 0.2s', display: 'inline-block' }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <FaInstagram size={18} />
                            </a>
                          )}
                          {speaker.social_links.linkedin && (
                            <a 
                              href={speaker.social_links.linkedin} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#00ffff', transition: 'transform 0.2s', display: 'inline-block' }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <FaLinkedin size={18} />
                            </a>
                          )}
                          {speaker.social_links.website && (
                            <a 
                              href={speaker.social_links.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#00ffff', transition: 'transform 0.2s', display: 'inline-block' }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              <FaGlobe size={18} />
                            </a>
                          )}
                        </div>
                      )}
                      
                      {/* Click hint to flip back */}
                      <div style={{
                        marginTop: '16px',
                        fontSize: '12px',
                        color: '#00ffff',
                        textAlign: 'center',
                        opacity: 0.7
                      }}>
                        {t('speakers.clickToReturn') || '🔄 Click to return'}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Attend */}
      <section className="section-dark">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('whyAttend.title')}</h2>
            <div className="red-line"></div>
          </div>
          <div className="why-attend-grid">
            {t('whyAttend.items', { returnObjects: true }).map((reason, index) => (
              <div key={index} className="why-item">
                <CheckCircle2 className="why-icon" />
                <p>{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section id="program" className="section-dark">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('schedule.title')}</h2>
            <div className="red-line"></div>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#00ffff' }}>
              {t('common.loadingData')}
            </div>
          ) : schedule.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              {t('schedule.comingSoon')}
            </div>
          ) : (
            <div className="program-timeline">
              {schedule.map((item, index) => (
              <div key={index} className="program-item">
                <div className="program-time">{item.time}</div>
                <div className="program-indicator"></div>
                <div className="program-details">
                  <h3 className="program-title">{item.title}</h3>
                  {item.speakers && item.speakers.length > 0 ? (
                    <div className="program-panel-speakers">
                      <p className="program-panel-label">👥 {t('schedule.panelParticipants')}</p>
                      <ul className="program-panel-list">
                        {item.speakers.map((speaker, idx) => (
                          <li key={idx} className="program-panel-speaker">{speaker}</li>
                        ))}
                      </ul>
                    </div>
                  ) : item.speaker && (
                    <p className="program-speaker">{t('schedule.speaker')}: {item.speaker}</p>
                  )}
                  {item.description && <p className="program-description">{item.description}</p>}
                  <span className={`program-badge ${item.type}`}>
                    {t(`schedule.types.${item.type}`, item.type.toUpperCase())}
                  </span>
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* Partners */}
      <section id="partners" className="section-dark partners-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('partners.title')}</h2>
            <div className="red-line"></div>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#00ffff' }}>
              {t('common.loadingData')}
            </div>
          ) : partners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              {t('partners.comingSoon')}
            </div>
          ) : (
            <div className="partners-grid">
              {partners.map((partner) => (
              <div key={partner.id} className="partner-card">
                {partner.website_url ? (
                  <a 
                    href={partner.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="partner-link"
                  >
                    <div className="partner-logo-wrapper">
                      <img src={partner.logo} alt={partner.name} className="partner-logo" />
                      {partner.tier !== 'none' && <span className={`partner-tier ${partner.tier}`}>{partner.tier}</span>}
                    </div>
                  </a>
                ) : (
                  <div className="partner-logo-wrapper">
                    <img src={partner.logo} alt={partner.name} className="partner-logo" />
                    {partner.tier !== 'none' && <span className={`partner-tier ${partner.tier}`}>{partner.tier}</span>}
                  </div>
                )}
              </div>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* Media Partners - Cyber Style */}
      <section className="section-dark media-partners-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('mediaPartners.title')}</h2>
            <div className="red-line"></div>
            <p className="section-subtitle">{t('mediaPartners.subtitle')}</p>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#00ffff' }}>
              {t('common.loadingData')}
            </div>
          ) : mediaPartners.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              {t('mediaPartners.comingSoon')}
            </div>
          ) : (
            <div className="media-partners-grid">
              {mediaPartners.map((mediaPartner, index) => (
              <div 
                key={mediaPartner.id} 
                className="media-partner-card"
              >
                {mediaPartner.website_url ? (
                  <a 
                    href={mediaPartner.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="media-partner-link"
                  >
                    <div className="media-partner-logo-wrapper">
                      <img src={mediaPartner.logo} alt={mediaPartner.name} className="media-partner-logo" />
                      <div className="cyber-overlay"></div>
                    </div>
                    <div className="media-partner-name">{mediaPartner.name}</div>
                  </a>
                ) : (
                  <>
                    <div className="media-partner-logo-wrapper">
                      <img src={mediaPartner.logo} alt={mediaPartner.name} className="media-partner-logo" />
                      <div className="cyber-overlay"></div>
                    </div>
                    <div className="media-partner-name">{mediaPartner.name}</div>
                  </>
                )}
              </div>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* Venue */}
      <section className="section-dark venue-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('venue.title')}</h2>
            <div className="red-line"></div>
            <p className="section-subtitle" style={{ color: '#00ffff', marginTop: '10px' }}>
              {siteSettings.venue_revealed ? t('venue.subtitleRevealed') : t('venue.subtitle')}
            </p>
          </div>
          
          {!siteSettings.venue_revealed ? (
            // ЗАКРИТА ЛОКАЦІЯ
            <div className="venue-content">
              <div className="venue-info">
                <div style={{ 
                  background: 'linear-gradient(135deg, #1a1a4d 0%, #1a4d4d 100%)', 
                  padding: '30px', 
                  borderRadius: '12px',
                  border: '2px solid #00ffff',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ 
                      background: '#00ffff', 
                      borderRadius: '50%', 
                      width: '48px', 
                      height: '48px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <MapPin size={24} color="#000" />
                    </div>
                    <h3 style={{ color: '#00ffff', fontSize: '28px', margin: 0 }}>{t('venue.secretLocation')}</h3>
                  </div>
                  
                  <div style={{ 
                    background: 'rgba(0, 255, 255, 0.1)', 
                    padding: '20px', 
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid rgba(0, 255, 255, 0.3)'
                  }}>
                    <p style={{ color: '#fff', fontSize: '16px', margin: 0, lineHeight: '1.6' }}>
                      {t('venue.secretInfo')}
                    </p>
                  </div>

                  <div className="venue-detail" style={{ marginBottom: '15px' }}>
                    <MapPin className="venue-icon" style={{ color: '#00ffff' }} />
                    <span style={{ fontSize: '16px' }}><strong style={{ color: '#00ffff' }}>{t('venue.city')}</strong> {t('venue.cityName')}</span>
                  </div>
                  
                  <div className="venue-detail" style={{ marginBottom: '15px' }}>
                    <Calendar className="venue-icon" style={{ color: '#00ffff' }} />
                    <span style={{ fontSize: '16px' }}><strong style={{ color: '#00ffff' }}>{t('venue.date')}</strong> {t('venue.dateName')}</span>
                  </div>

                  <div style={{ 
                    background: 'rgba(255, 87, 87, 0.1)', 
                    padding: '15px', 
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 87, 87, 0.3)',
                    marginTop: '20px'
                  }}>
                    <p style={{ color: '#ff5757', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                      ⚠️ <strong>{t('venue.warningTitle')}</strong> {t('venue.warningText')}
                    </p>
                  </div>
                </div>

                <div style={{ 
                  background: 'rgba(26, 26, 77, 0.5)', 
                  padding: '25px', 
                  borderRadius: '12px',
                  border: '1px solid #333'
                }}>
                  <h4 style={{ color: '#00ffff', marginBottom: '15px', fontSize: '20px' }}>
                    {t('venue.accessTitle')}
                  </h4>
                  <ul style={{ color: '#ccc', lineHeight: '2', paddingLeft: '20px', margin: 0 }}>
                    <li dangerouslySetInnerHTML={{ __html: t('venue.accessList.free') }} />
                    <li dangerouslySetInnerHTML={{ __html: t('venue.accessList.open') }} />
                    <li dangerouslySetInnerHTML={{ __html: t('venue.accessList.selected') }} />
                    <li>{t('venue.accessList.invitation')}</li>
                  </ul>
                </div>
              </div>
              <div className="venue-secret-container">
                <div className="venue-secret-badge">
                  <span className="lock-icon">🔒</span>
                  {t('venue.secretBadge')}
                </div>
                
                {/* Cyber animated background */}
                <div className="cyber-lock-animation">
                  <div className="cyber-grid"></div>
                  <div className="cyber-scan-line"></div>
                  <div className="cyber-hexagon">
                    <div className="hex-inner">
                      <div className="hex-lock-icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C9.79 2 8 3.79 8 6V8H7C5.9 8 5 8.9 5 10V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V10C19 8.9 18.1 8 17 8H16V6C16 3.79 14.21 2 12 2ZM12 4C13.1 4 14 4.9 14 6V8H10V6C10 4.9 10.9 4 12 4ZM12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13Z" fill="currentColor"/>
                        </svg>
                      </div>
                      <div className="hex-pulse"></div>
                    </div>
                  </div>
                  <div className="cyber-particles">
                    {[...Array(20)].map((_, i) => (
                      <div key={i} className="particle" style={{
                        '--delay': `${i * 0.5}s`,
                        '--duration': `${3 + Math.random() * 2}s`,
                        '--x': `${Math.random() * 100}%`,
                        '--y': `${Math.random() * 100}%`
                      }}></div>
                    ))}
                  </div>
                  <div className="cyber-text">
                    <span className="glitch" data-text="CLASSIFIED">CLASSIFIED</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // РОЗКРИТА ЛОКАЦІЯ - Cyberpunk Style
            <div className="venue-content">
              <div className="venue-info">
                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.1) 0%, rgba(0, 100, 255, 0.05) 100%)', 
                  padding: '30px', 
                  borderRadius: '12px',
                  border: '2px solid rgba(0, 255, 255, 0.3)',
                  marginBottom: '20px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Cyberpunk grid background */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 255, 0.03) 2px, rgba(0, 255, 255, 0.03) 4px)',
                    pointerEvents: 'none',
                    zIndex: 0
                  }} />
                  
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                      <div style={{ 
                        background: 'rgba(0, 255, 255, 0.2)', 
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid rgba(0, 255, 255, 0.4)',
                        boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)'
                      }}>
                        <MapPin size={28} color="#00ffff" />
                      </div>
                      <div>
                        <h3 style={{ color: '#00ffff', fontSize: '28px', margin: 0, fontWeight: '700', textShadow: '0 0 10px rgba(0, 255, 255, 0.5)' }}>
                          {i18n.language === 'uk' ? (siteSettings.venue_name || 'КВЦ Парковий') : (siteSettings.venue_name_en || 'Parkovy Convention and Exhibition Center')}
                        </h3>
                        <p style={{ color: '#888', fontSize: '14px', margin: '4px 0 0 0', textTransform: 'uppercase', letterSpacing: '2px' }}>
                          {t('venue.revealedTitle')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="venue-detail" style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ 
                        background: 'rgba(0, 255, 255, 0.1)', 
                        padding: '8px', 
                        borderRadius: '6px',
                        border: '1px solid rgba(0, 255, 255, 0.2)'
                      }}>
                        <MapPin className="venue-icon" size={20} style={{ color: '#00ffff' }} />
                      </div>
                      <span style={{ fontSize: '16px', color: '#ccc' }}>
                        {i18n.language === 'uk' ? (siteSettings.venue_address || 'Паркова дорога, 16а, 01021, Київ, Україна') : (siteSettings.venue_address_en || 'Parkova Doroha, 16a, 01021, Kyiv, Ukraine')}
                      </span>
                    </div>
                    
                    <div className="venue-detail" style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ 
                        background: 'rgba(0, 255, 255, 0.1)', 
                        padding: '8px', 
                        borderRadius: '6px',
                        border: '1px solid rgba(0, 255, 255, 0.2)'
                      }}>
                        <Calendar className="venue-icon" size={20} style={{ color: '#00ffff' }} />
                      </div>
                      <span style={{ fontSize: '16px', color: '#ccc' }}>
                        <strong style={{ color: '#00ffff' }}>{t('venue.date')}</strong> {t('venue.dateName')}
                      </span>
                    </div>

                    <div className="venue-detail" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ 
                        background: 'rgba(0, 255, 255, 0.1)', 
                        padding: '8px', 
                        borderRadius: '6px',
                        border: '1px solid rgba(0, 255, 255, 0.2)'
                      }}>
                        <ExternalLink className="venue-icon" size={20} style={{ color: '#00ffff' }} />
                      </div>
                      <a 
                        href={siteSettings.venue_url || 'https://parkovy.com.ua/'} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          fontSize: '16px', 
                          color: '#00ffff', 
                          textDecoration: 'none',
                          borderBottom: '1px solid rgba(0, 255, 255, 0.3)',
                          paddingBottom: '2px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderBottomColor = '#00ffff';
                          e.currentTarget.style.textShadow = '0 0 10px rgba(0, 255, 255, 0.8)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderBottomColor = 'rgba(0, 255, 255, 0.3)';
                          e.currentTarget.style.textShadow = 'none';
                        }}
                      >
                        {t('venue.websiteLink')}
                      </a>
                    </div>

                    <div style={{ 
                      background: 'rgba(0, 255, 255, 0.05)', 
                      padding: '15px 20px', 
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 255, 255, 0.2)',
                      borderLeft: '4px solid #00ffff'
                    }}>
                      <p style={{ color: '#00ffff', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                        {t('venue.revealedText')}
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ 
                  background: 'rgba(0, 0, 0, 0.4)', 
                  padding: '25px', 
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 255, 255, 0.2)'
                }}>
                  <h4 style={{ color: '#00ffff', marginBottom: '15px', fontSize: '20px', fontWeight: '600' }}>
                    {t('venue.howToGetTitle')}
                  </h4>
                  <ul style={{ color: '#ccc', lineHeight: '2', paddingLeft: '20px', margin: 0, listStyle: 'none' }}>
                    <li style={{ marginBottom: '12px', paddingLeft: '24px', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#00ffff', fontSize: '18px' }}>→</span>
                      {t('venue.howToGet.metro')}
                    </li>
                    <li style={{ marginBottom: '12px', paddingLeft: '24px', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#00ffff', fontSize: '18px' }}>→</span>
                      {t('venue.howToGet.parking')}
                    </li>
                    <li style={{ paddingLeft: '24px', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#00ffff', fontSize: '18px' }}>→</span>
                      {t('venue.howToGet.public')}
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="venue-image" style={{ 
                position: 'relative', 
                borderRadius: '12px', 
                overflow: 'hidden', 
                border: '2px solid rgba(0, 255, 255, 0.3)',
                boxShadow: '0 0 30px rgba(0, 255, 255, 0.2)'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  right: '10px',
                  height: '40px',
                  background: 'rgba(0, 0, 0, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  gap: '8px'
                }}>
                  <MapPin size={18} color="#00ffff" />
                  <span style={{ color: '#00ffff', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Location Map
                  </span>
                </div>
                <iframe
                  width="100%"
                  height="500"
                  style={{ border: 0, filter: 'brightness(0.9) contrast(1.1)' }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${siteSettings.venue_lat || 50.4356},${siteSettings.venue_lng || 30.5463}&zoom=15`}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Registration */}
      <section id="register" className="section-dark registration-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('registration.title')}</h2>
            <div className="red-line"></div>
            <p className="section-subtitle">{t('registration.subtitle')}</p>
          </div>
          <div className="registration-buttons">
            <div className="registration-card">
              <div className="registration-card-icon">
                <Mic size={48} />
              </div>
              <h3 className="registration-card-title">{t('registration.speaker.title')}</h3>
              <p className="registration-card-description">
                {t('registration.speaker.description')}
              </p>
              <Button className="btn-primary btn-register-card" onClick={handleSpeakerRegister}>
                {t('registration.speaker.button')}
              </Button>
            </div>
            <div className="registration-card">
              <div className="registration-card-icon">
                <Users size={48} />
              </div>
              <h3 className="registration-card-title">{t('registration.participant.title')}</h3>
              <p className="registration-card-description">
                {t('registration.participant.description')}
              </p>
              <Button className="btn-secondary btn-register-card" onClick={handleParticipantRegister}>
                {t('registration.participant.button')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3 
                style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  color: '#ffffff',
                  marginBottom: '12px',
                  letterSpacing: '2px'
                }}
              >
                KICRF
              </h3>
              <p className="footer-text">{t('footer.about.description')}</p>
              <p className="footer-text" style={{ marginTop: '8px', fontSize: '12px' }}>
                {t('footer.about.partOf')} <a href="https://cyberforumkyiv.org/" target="_blank" rel="noopener noreferrer" style={{ color: '#00ffff', textDecoration: 'underline' }}>Київського Міжнародного Форуму з Кіберстійкості</a>
              </p>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">{t('footer.contact.title')}</h4>
              <a href="mailto:info@cyberforumkyiv.com" className="footer-link">info@cyberforumkyiv.com</a>
              <a href="mailto:press@cyberforumkyiv.com" className="footer-link">press@cyberforumkyiv.com</a>
              <a href="mailto:partners@cyberforumkyiv.com" className="footer-link">partners@cyberforumkyiv.com</a>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">{t('footer.quickLinks.title')}</h4>
              <a href="#about" className="footer-link">{t('footer.quickLinks.about')}</a>
              <a href="#topics" className="footer-link">{t('nav.topics')}</a>
              <a href="#speakers" className="footer-link">{t('footer.quickLinks.speakers')}</a>
              <a href="#register" className="footer-link">{t('nav.register')}</a>
              <a href="https://cyberforumkyiv.org/" target="_blank" rel="noopener noreferrer" className="footer-link">{t('footer.quickLinks.mainSite')}</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <div 
        className={`back-to-top ${showButton ? 'visible' : ''}`}
        onClick={scrollToTop}
      >
        <ChevronUp size={24} color="#00ffff" />
      </div>
    </div>
  );
};

export default Home;
